import { BaseOrganizationService } from '@/modules/core/BaseOrganizationService';
import { SupabaseClient } from '@supabase/supabase-js';

export interface Event {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  start_date: string;
  end_date: string;
  description?: string;
  image_url?: string;
  thumbnail_url?: string;
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  registration_type?: 'individual' | 'team';
  registration_open_date?: string;
  registration_close_date?: string;
  pricing_notes?: string;
  gender?: string;
  sport_id?: string;
  event_format?: string;
  accept_online_registrations: boolean;
  status: 'draft' | 'published';
  event_config?: any;
  event_category_ids?: string[];
  created_at: string;
  updated_at: string;
}

export class EventService extends BaseOrganizationService<Event> {
  constructor(supabase: SupabaseClient, organizationId: string) {
    super(supabase, organizationId, 'events');
  }

  /**
   * Get upcoming events (not started yet) - public view, published only
   */
  async getUpcoming(): Promise<Event[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await this.baseQuery()
      .gte('start_date', today)
      .eq('status', 'published')
      .order('start_date', { ascending: true });
    
    if (error) throw error;
    return data;
  }

  /**
   * Get upcoming events for admin - includes all statuses (draft, published)
   * Joins with venues table to get location when venue_id is set
   */
  async getUpcomingAdmin(): Promise<Event[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await this.supabase
      .from('events')
      .select(`
        *,
        venues:venue_id(name, city, state)
      `)
      .eq('organization_id', this.organizationId)
      .gte('start_date', today)
      .order('start_date', { ascending: true });
    
    if (error) throw error;
    
    // Merge venue data into event fields
    return (data || []).map(event => ({
      ...event,
      venue_name: event.venue_name || (event.venues as any)?.name,
      venue_city: event.venue_city || (event.venues as any)?.city,
      venue_state: event.venue_state || (event.venues as any)?.state,
    }));
  }

  /**
   * Get past events (includes all past events, published and unpublished)
   * Joins with venues table to get location when venue_id is set
   */
  async getPast(): Promise<Event[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await this.supabase
      .from('events')
      .select(`
        *,
        venues:venue_id(name, city, state)
      `)
      .eq('organization_id', this.organizationId)
      .lt('end_date', today)
      .order('start_date', { ascending: false });
    
    if (error) throw error;
    
    // Merge venue data into event fields
    return (data || []).map(event => ({
      ...event,
      venue_name: event.venue_name || (event.venues as any)?.name,
      venue_city: event.venue_city || (event.venues as any)?.city,
      venue_state: event.venue_state || (event.venues as any)?.state,
    }));
  }

  /**
   * Get all events for admin - includes all statuses
   * Joins with venues table to get location when venue_id is set
   */
  async getAll(): Promise<Event[]> {
    const { data, error } = await this.supabase
      .from('events')
      .select(`
        *,
        venues:venue_id(name, city, state)
      `)
      .eq('organization_id', this.organizationId)
      .order('start_date', { ascending: true });
    
    if (error) throw error;
    
    // Merge venue data into event fields
    return (data || []).map(event => ({
      ...event,
      venue_name: event.venue_name || (event.venues as any)?.name,
      venue_city: event.venue_city || (event.venues as any)?.city,
      venue_state: event.venue_state || (event.venues as any)?.state,
    }));
  }

  /**
   * Get event by slug (for public URLs)
   */
  async getBySlug(slug: string): Promise<Event | null> {
    const { data, error } = await this.baseQuery()
      .eq('slug', slug)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  /**
   * Get event with full details (pricing, venue, etc.)
   */
  async getWithDetails(eventId: string) {
    const { data, error } = await this.baseQuery()
      .eq('id', eventId)
      .select(`
        *,
        pricing:event_pricing(pricing_structure),
        venues:event_venues(*)
      `)
      .single();
    
    if (error) throw error;
    return data;
  }

  /**
   * Get events accepting registrations
   */
  async getAcceptingRegistrations(): Promise<Event[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await this.baseQuery()
      .eq('accept_online_registrations', true)
      .eq('status', 'published')
      .gte('end_date', today)
      .order('start_date', { ascending: true });
    
    if (error) throw error;
    return data;
  }

  /**
   * Search events by name
   */
  async search(query: string): Promise<Event[]> {
    const { data, error } = await this.baseQuery()
      .ilike('name', `%${query}%`)
      .eq('status', 'published')
      .order('start_date', { ascending: true })
      .limit(20);
    
    if (error) throw error;
    return data;
  }

  /**
   * Get registration count for event
   */
  async getRegistrationCount(eventId: string): Promise<number> {
    const { count, error } = await this.supabase
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('organization_id', this.organizationId)
      .eq('status', 'confirmed');
    
    if (error) throw error;
    return count || 0;
  }

  /**
   * Check if event has available spots
   */
  async hasAvailableSpots(eventId: string): Promise<boolean> {
    // Get event config
    const event = await this.getById(eventId);
    if (!event) return false;

    const maxRegistrations = event.event_config?.max_registrations;
    if (!maxRegistrations) return true; // No limit

    // Get current count
    const currentCount = await this.getRegistrationCount(eventId);
    return currentCount < maxRegistrations;
  }

  /**
   * Get events by date range
   */
  async getByDateRange(startDate: string, endDate: string): Promise<Event[]> {
    const { data, error } = await this.baseQuery()
      .gte('start_date', startDate)
      .lte('start_date', endDate)
      .eq('status', 'published')
      .order('start_date', { ascending: true });
    
    if (error) throw error;
    return data;
  }
}
